import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import { format, isValid, parseISO } from "date-fns";
import styled, { keyframes } from "styled-components";
import { DayPicker, Matcher } from "react-day-picker";
import "react-day-picker/dist/style.css";
import Input from "../../../components/Input";
import { CalendarIcon } from "@heroicons/react/24/outline";

interface DatePickerPropsType {
  formatDate?: string;
  selected: any;
  disabled?: boolean;
  disabledSelectDate?: Matcher | Matcher[];
  onChange: (date: string) => void;
  multiMonth?: boolean;
  allowRange?: boolean;
  placeholder?: string;
  autoOpen?: boolean; // open dropdown immediately when entering edit mode
  variant?: "default" | "inline";
  showTriggerHeight?: boolean; // Whether to adjust dropdown position based on trigger height
  customTrigger?: React.ReactNode; // Custom trigger element to replace default input
}

const cardLaneAnimation = keyframes`
 0% { transform: translate3d(0px, -48px, 0px) }
 100% { transform: translate3d(0px, 0px, 0px) }
`;

export const StyledDateContainer = styled.section`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  animation: 100ms cubic-bezier(0.1, 0.9, 0.2, 1) 0s 1 normal none running ${cardLaneAnimation};
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);

  .rdp-root {
    --rdp-accent-color: #3b82f6;
    --rdp-accent-background-color: #eff6ff;
    --rdp-day-height: 40px;
    --rdp-day-width: 40px;
    --rdp-day_button-height: 40px;
    --rdp-day_button-width: 40px;
    --rdp-day_button-border-radius: 20px;
    --rdp-outside-opacity: 0.5;
    --rdp-today-color: #f59e0b;
    --rdp-nav_button-height: 32px;
    --rdp-nav_button-width: 32px;
    --rdp-weekday-opacity: 0.7;
  }

  .rdp-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px 16px 24px;
    background: white;
    border-bottom: 1px solid #f3f4f6;
  }

  .rdp-nav_button {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: white;
    color: #374151;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      background: #f9fafb;
      border-color: #d1d5db;
      color: #111827;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }

  .rdp-caption {
    font-weight: 700;
    font-size: 16px;
    color: #111827;
    text-align: center;
    margin: 0;
    padding: 0 16px;
  }

  .rdp-month {
    background: white;
    padding: 16px 20px 20px 20px;
  }

  .rdp-tbody_cell {
    padding: 4px;
    text-align: center;
  }

  .rdp-button {
    width: 40px;
    height: 40px;
    border-radius: 20px;
    border: none;
    background: transparent;
    color: #374151;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;

    &:hover {
      background: #f3f4f6;
      color: #111827;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
  }

  .rdp-day_outside {
    color: #9ca3af !important;
    opacity: 0.5;
  }

  .rdp-day_today {
    background: #fef3c7 !important;
    color: #92400e !important;
    font-weight: 600;
  }

  .rdp-day_selected {
    background: #3b82f6 !important;
    color: white !important;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
  }

  .rdp-day_selected:hover {
    background: #2563eb !important;
  }

  .rdp-day_disabled {
    color: #d1d5db !important;
    cursor: not-allowed;
    opacity: 0.5;
  }

  .rdp-day_disabled:hover {
    background: transparent !important;
  }

  .rdp {
    margin: 0 !important;
    font-family:
      "Inter",
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      Roboto,
      "Helvetica Neue",
      Arial,
      sans-serif;
  }
`;

const DatePicker = (props: DatePickerPropsType) => {
  const {
    selected,
    onChange,
    placeholder,
    disabled,
    disabledSelectDate,
    autoOpen,
    variant = "default",
    showTriggerHeight = false,
    customTrigger,
  } = props;
  const [value, setValue] = useState<string | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const customTriggerRef = useRef<HTMLElement | null>(null);
  const [, setTriggerHeight] = useState<number>(0);

  useEffect(() => {
    if (!selected) {
      setValue("");
      return;
    }

    const isPlausibleDate = (d: Date) => {
      if (!isValid(d)) return false;
      const year = d.getFullYear();
      return year >= 1990 && year <= 2100;
    };

    try {
      let selectedDate: Date | undefined;
      const t = typeof selected;

      if (t === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(selected as string)) {
          selectedDate = parseISO(selected as string);
        } else {
          selectedDate = new Date(selected as string);
        }
      } else if (t === "number") {
        const num = selected as number;
        const ms = num < 1e12 ? num * 1000 : num;
        selectedDate = new Date(ms);
      } else {
        selectedDate = new Date(selected as any);
      }

      if (!isValid(selectedDate) || !isPlausibleDate(selectedDate)) {
        return;
      }

      setValue(format(selectedDate, "yyyy-MM-dd"));
    } catch {
      return;
    }
  }, [selected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (isOpen && dropdownRef.current) {
        const newStyle = getDropdownStyle();
        if (newStyle.left && newStyle.top) {
          dropdownRef.current.style.left = newStyle.left;
          dropdownRef.current.style.top = newStyle.top;
        }
      }
    };

    const handleScroll = () => {
      if (isOpen && dropdownRef.current) {
        const newStyle = getDropdownStyle();
        if (newStyle.left && newStyle.top) {
          dropdownRef.current.style.left = newStyle.left;
          dropdownRef.current.style.top = newStyle.top;
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (autoOpen && !disabled) {
      setIsOpen(true);
    }
  }, [autoOpen, disabled]);

  // Calculate trigger height when it's available
  useEffect(() => {
    if (showTriggerHeight) {
      const rafId = requestAnimationFrame(() => {
        if (customTrigger && customTriggerRef.current) {
          setTriggerHeight(customTriggerRef.current.offsetHeight);
        } else if (containerRef.current) {
          setTriggerHeight(containerRef.current.offsetHeight);
        }
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [showTriggerHeight, isOpen, customTrigger]);

  const getDropdownStyle = () => {
    if (!containerRef.current || !isOpen) {
      return {};
    }

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const dropdownWidth = 320;
    const dropdownHeight = 400;
    const margin = 20;

    let left = rect.left;
    let top = showTriggerHeight ? rect.top - 4 : rect.bottom + 8;

    if (left + dropdownWidth + margin > viewportWidth) {
      left = viewportWidth - dropdownWidth - margin;
    }

    if (top + dropdownHeight + margin > viewportHeight) {
      top = rect.top - dropdownHeight - 8;
    }

    if (left < margin) {
      left = margin;
    }
    if (top < margin) {
      top = margin;
    }

    if (left > viewportWidth || top > viewportHeight) {
      setIsOpen(false);
      return {};
    }

    return {
      position: "fixed" as const,
      left: `${left}px`,
      top: `${top}px`,
      zIndex: 1000,
    };
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !disabled) {
      submitValue();
    }
  };

  const submitValue = () => {
    if (!value || value.trim() === "") {
      return;
    }

    try {
      const newDate = new Date(value);
      if (isValid(newDate) && !disabled) {
        onChange(value);
      }
    } catch {
      // ignore invalid manual input
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleDateSelect = (selectedDate: any) => {
    if (!disabled && selectedDate) {
      try {
        const formattedDate = format(selectedDate, "yyyy-MM-dd");
        onChange(formattedDate);
        setIsOpen(false);
      } catch {
        // ignore
      }
    }
  };

  // Helper: Forward ref for custom trigger element
  const forwardCustomTriggerRef = (node: HTMLElement | null) => {
    customTriggerRef.current = node;
    if (React.isValidElement(customTrigger)) {
      const originalRef = (customTrigger as any).ref;
      if (originalRef) {
        if (typeof originalRef === "function") {
          originalRef(node);
        } else {
          originalRef.current = node;
        }
      }
    }
  };

  const isInline = variant === "inline";
  const containerClass = isInline
    ? "flex items-center w-full rounded-md border border-gray-300 px-2 py-1 min-h-[28px] cursor-pointer relative"
    : "flex cursor-pointer w-full align-middle relative";

  const inputClass = isInline
    ? "block w-full bg-white text-[12px] leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none pr-8"
    : "pr-10";

  return (
    <div ref={containerRef}>
      {customTrigger ? (
        <div
          ref={(node) => {
            if (React.isValidElement(customTrigger)) {
              // For React elements, we'll handle ref in cloneElement
            } else {
              customTriggerRef.current = node;
            }
          }}
          onClick={handleInputClick}
          className="cursor-pointer"
        >
          {React.isValidElement(customTrigger) ? (
            React.cloneElement(customTrigger as any, {
              ref: forwardCustomTriggerRef,
              onClick: (e: React.MouseEvent) => {
                handleInputClick();
                (customTrigger as any).props?.onClick?.(e);
              },
            })
          ) : (
            <div
              ref={(node) => {
                customTriggerRef.current = node;
              }}
            >
              {customTrigger}
            </div>
          )}
        </div>
      ) : (
        <div className={containerClass} onClick={handleInputClick}>
          {isInline ? (
            <input
              value={value || ""}
              onChange={(e) => setValue(e.target.value)}
              onKeyPress={handleKeyPress}
              onBlur={submitValue}
              placeholder={placeholder || "Select date"}
              className={inputClass}
              readOnly
            />
          ) : (
            <Input
              value={value || ""}
              onChange={setValue}
              onKeyPress={handleKeyPress}
              onBlur={submitValue}
              placeholder={placeholder || "Select date"}
              className="pr-10"
              styleInput="text-sm font-medium"
              readOnly
            />
          )}
          <CalendarIcon
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 ${
              isInline ? "w-4 h-4" : "w-5 h-5"
            }`}
          />
        </div>
      )}

      {isOpen && (
        <div ref={dropdownRef} style={getDropdownStyle()} className="overflow-hidden">
          <StyledDateContainer className="w-80 max-w-[calc(100vw-2rem)] min-w-[280px]">
            <DayPicker
              mode="single"
              onSelect={handleDateSelect}
              selected={value ? new Date(value) : undefined}
              showOutsideDays={true}
              fixedWeeks={true}
              disabled={disabledSelectDate}
              className="modern-datepicker"
            />
          </StyledDateContainer>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
